import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import {
  ListHelperService,
  ProfileService,
  StoreService,
  LicensesService,
  PopUpService,
  GamesService,
  ArcadeService
} from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { License } from 'core/models/license';
import { LicenseCloneService } from '../services/license-clone.service';

@Injectable()
export class LicensesListService {
  userRole: any;
  constructor(
    public licensesService: LicensesService,
    private arcadeService: ArcadeService,
    private licenseCloneService: LicenseCloneService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    private popupService: PopUpService,
    private gamesService: GamesService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  async getDatasource(listId, options: any = {}) {
    const dataSource = new TableDatasource({
      sort: 'userEmail',
      columns: this.getColumns(),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { groupId, userId, kind, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      groupId,
      userId,
      kind,
      $filter,
      // Get licenses' courseTitle translated to user's language
      $lang: this.profileService.getLang()
    };
    return this.licensesService.fetch(paginationOptions).then((response) => response);
  }

  getDatasourceFilters(dataSource: TableDatasource) {
    const { groupId, userId, kind, ...$filterValues } = dataSource.filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    return {
      groupId,
      userId,
      kind,
      $filter
    };
  }

  getColumns(sortable: boolean = true) {
    const columns = [
      {
        source: 'userEmail',
        label: this.translate.instant('nouns.email'),
        sortable: sortable,
        style: { width: '200px' },
        cellRenderer: (renderer, value, license) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/students/${license.userId}`,
            value
          )}</div>`
      },
      {
        source: 'groupName',
        label: this.translate.instant('licenses.header.groupName'),
        sortable: sortable,
        style: { width: '200px' },
        cellRenderer: (renderer, value, license) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/groups/${license.groupId}`,
            value
          )}</div>`
      },
      {
        source: 'alias',
        label: this.translate.instant('licenses.header.alias'),
        sortable: sortable,
        style: { width: '120px' }
      },
      {
        source: 'courseTitle',
        label: this.translate.instant('licenses.header.gameName'),
        sortable: sortable,
        style: { width: '200px' }
      },
      {
        source: 'lang',
        label: this.translate.instant('licenses.header.lang'),
        sortable: sortable,
        style: { width: '120px' }
      },
      {
        source: 'startDate',
        label: this.translate.instant('licenses.header.startDate'),
        dataType: DataTypes.datetime,
        sortable: sortable,
        style: { width: '120px' },
        valueParser: (value, license) => this.listHelper.parseDate(value, license.timezone)
      },
      {
        source: 'endDate',
        label: this.translate.instant('licenses.header.endDate'),
        dataType: DataTypes.datetime,
        sortable: sortable,
        style: { width: '120px' },
        valueParser: (value, license) => {
          const licenseHasStarted =
            !!license &&
            license.status !== 'cancelled' &&
            !!value &&
            new Date(license.startDate).getTime() < Date.now();
          return licenseHasStarted ? this.listHelper.parseDate(value, license.timezone) : '';
        }
      },
      {
        source: 'status',
        label: this.translate.instant('licenses.header.status'),
        sortable: false,
        style: { width: '200px' },
        valueParser: (value) => (value ? this.translate.instant('licenses.state.' + value) : ''),
        cellRenderer: (renderer, status, license) =>
          renderer`
            <span style="color: var(${['created'].includes(license.status) ? '--coral' : '--charcoal-grey'});">
              ${status}
            </span>`,
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'created', value: this.translate.instant('licenses.state.created') },
            { key: 'completed', value: this.translate.instant('licenses.state.completed') },
            { key: 'cancelled', value: this.translate.instant('licenses.state.cancelled') },
            { key: 'beforeDemo', value: this.translate.instant('licenses.state.beforeDemo') },
            { key: 'afterDemo', value: this.translate.instant('licenses.state.afterDemo') }
          ];
          return renderer`
            <gl-select small multiselect
              .options="${options}"
              .value="${value || 'any'}"
              @onChange="${(e) => callback(e.detail.value)}"
            >
            </gl-select>`;
        }
      },
      {
        source: 'user.tags',
        label: this.translate.instant('licenses.header.tags'),
        style: { width: '220px' },
        dataType: DataTypes.array,
        allowFiltering: false
      },
      {
        alignment: 'right',
        showInColumnChooser: false,
        style: { width: '75px' },
        cellRenderer: (renderer, value, license) => {
          const today = Date.now();
          const isBonusClass = license && license.hasBonus;
          const invalidLicense = !license || license.kind !== 'normal' || license.status === 'cancelled';
          const completedLicense = license && license.status === 'completed';
          const licenseHasStarted =
            !invalidLicense && !!license.startDate && new Date(license.startDate).getTime() < today;
          const licenseHasFinished =
            (licenseHasStarted && new Date(license.endDate).getTime() < today) || completedLicense;
          const isActive =
            licenseHasStarted && !licenseHasFinished && (!license.isSuperlicense || !!license.currentSublicenseId);
          const isPossibleMoveLevel =
            !environment.oldGamesCanonicNames.includes(license.gameCanonicName) &&
            isActive &&
            !isBonusClass &&
            !completedLicense &&
            !invalidLicense;

          return renderer`
            <gl-context-menu medium>
              <gl-button slot="target" id="${license.id}" icon icon-center="glicon-dots"></gl-button>
              
              ${
                this.profileService.canExecute('licenses.detail')
                  ? renderer`<gl-menu-item text="${this.translate.instant('actions.edit')}"  @click="${() =>
                      this.listHelper.navigateTo(['/licenses', license.id])}"></gl-menu-item>`
                  : ''
              }
              
              ${
                this.profileService.canExecute('licenses.report')
                  ? renderer`<gl-menu-item text="${this.translate.instant('actions.showReport')}" @click="${() =>
                      this.listHelper.navigateTo(['/licenses', license.id, 'report'])}"></gl-menu-item>`
                  : ''
              }
              
              ${
                this.profileService.canExecute('licenses.reset-game') && isActive && license.status === 'beforeDemo'
                  ? renderer`<gl-menu-item text="${this.translate.instant('actions.resetGame')}" @click="${() =>
                      this.listHelper.run(() => this.resetGame(license))}"></gl-menu-item>`
                  : ''
              }
              
              ${
                this.profileService.canExecute('licenses.reset-level') && isPossibleMoveLevel
                  ? renderer`<gl-menu-item text="${this.translate.instant('actions.resetLevel')}" @click="${() =>
                      this.listHelper.run(() => this.resetLevel(license))}"></gl-menu-item>`
                  : ''
              }
              
              ${
                this.profileService.canExecute('licenses.pass-level') && isPossibleMoveLevel
                  ? renderer`<gl-menu-item text="${this.translate.instant('actions.passLevel')}" @click="${() =>
                      this.listHelper.run(() => this.passLevel(license))}"></gl-menu-item>`
                  : ''
              }

              ${
                this.profileService.canExecute('licenses.clone') && isActive
                  ? renderer`<gl-menu-item text="${this.translate.instant('actions.clone')}" @click="${() =>
                      this.cloneLicenseAndOpenGame(license)}"></gl-menu-item>`
                  : ''
              }

            </gl-context-menu>`;
        }
      }
    ];
    return columns;
  }

  getLicenseGameId(license): string {
    if (license.isSuperlicense && !!license.currentSublicenseId) {
      const indexCurrentSublicenseId = license.sublicenses.findIndex((x) => x == license.currentSublicenseId);
      return license.modules[indexCurrentSublicenseId].gameId;
    }
    return license.gameId;
  }

  resetGame(license: License) {
    this.popupService
      .confirmDelete({
        title: this.translate.instant('games.confirmResetGame'),
        confirmButtonText: this.translate.instant('actions.accept'),
        type: 'warning',
        class: 'center'
      })
      .then(() =>
        this.gamesService.resetGame(
          this.getLicenseGameId(license),
          license.currentSublicenseId ? license.currentSublicenseId : license.id
        )
      );
  }

  resetLevel(license: any) {
    const { user } = license;
    const textData = { email: user && user.fullName ? user.fullName : license.userEmail };
    this.popupService
      .confirm({
        title: this.translate.instant('licenses.resetLevel.title'),
        text: this.translate.instant('licenses.resetLevel.text', textData),
        alert: this.translate.instant('actions.notUndo'),
        confirmButtonText: this.translate.instant('actions.resetLevel'),
        size: 'small'
      })
      .then(() => {
        this.arcadeService.resetLevel(license.isSuperlicense ? license.currentSublicenseId : license.id);
      });
  }

  passLevel(license: any) {
    const { user } = license;
    const textData = { email: user && user.fullName ? user.fullName : license.userEmail };
    this.popupService
      .confirm({
        title: this.translate.instant('licenses.passLevel.title'),
        text: this.translate.instant('licenses.passLevel.text', textData),
        alert: this.translate.instant('actions.notUndo'),
        confirmButtonText: this.translate.instant('actions.passLevel'),
        size: 'small'
      })
      .then(() => {
        this.arcadeService.passLevel(license.isSuperlicense ? license.currentSublicenseId : license.id);
      });
  }

  cloneLicenseAndOpenGame(license) {
    this.licenseCloneService.cloneLicense(license);
  }

  getCustomFilters() {
    return {
      status: (filterBuilder, value) => (value && value.length ? filterBuilder.in('status', value) : null)
    };
  }
}
